import { modifyAgeType } from 'lib/apis/utils';
import * as Type from './types';

// ad info
export function transformAdInfo(apiData: Partial<Type.SearchResponseAdInfo>): Type.SearchAdInfo {
  const adInfo = {
    adUnit: apiData.ad_unit,
    make: apiData.make,
    model: apiData.model,
    year: apiData.year,
    pageType: apiData.page_type,
    ageType: modifyAgeType(apiData.year, apiData.age_type),
    cpo: apiData.cpo,
    // intentionally leaving this as snake case
    // https://autolist.slack.com/archives/CHA7W6PGF/p1701462625670549?thread_ts=1701452109.414969&cid=CHA7W6PGF
    body_style: apiData.body_style,
    app: apiData.app,
    sellerId: apiData.seller_id,
  };

  type AdInfoKey = keyof typeof adInfo;

  return Object.fromEntries(Object.entries(adInfo).filter(([, v]) => v !== undefined)) as {
    [K in AdInfoKey]?: typeof adInfo[K];
  } as Type.SearchAdInfo;
}

// vehicle

export function transformSrpVehicle(apiData: Type.SearchResponseVehicle): Type.SearchVehicle {
  // eslint-disable-next-line camelcase
  const { city, state, created_at } = apiData;
  const createdAt = Date.parse(created_at);
  const currentDate = Date.parse(new Date().toDateString());
  return {
    id: apiData.id,
    vin: apiData.vin,
    hasImages: apiData.has_photos,
    images: apiData.photo_urls,
    make: apiData.make_name,
    model: apiData.model_name,
    year: apiData.year,
    city,
    state,
    justListed: currentDate - createdAt < 86400000,
    mileage: apiData.mileage,
    showNewMileage: apiData.show_new_mileage,
    price: apiData.price,
    location: `${city}, ${state}`,
    dealerName: apiData.dealer_name,
    isHot: apiData.is_hot,
    hasRecentPriceDrop: apiData.recent_price_drop,
    trackingParams: apiData.tracking_params,
    isRegional: apiData.regional,
    availableNationwide: apiData.available_nationwide,
    carmax: apiData.provider_name === 'carmax',
    partnerType: apiData.partner_type,
    cplValue: apiData.cpl_value,
    // zip: apiData.zip || '',
    buyerIntelligence: {
      relativePriceDiff: apiData.relative_price_difference,
      totalPriceChange: apiData.total_price_change,
      timeOnMarket: apiData.time_on_market,
    },
    isQuickPicksEligible: apiData.quick_picks_eligible,
    trim: apiData.trim,
    vdpUrl: apiData.vdp_url || `${apiData.make_name}-${apiData.model_name}#vin=${apiData.vin}`,
    bodyType: apiData.body_type,
    doorCount: apiData.door_count,
    driveline: apiData.driveline,
    fuelType: apiData.fuel_type,
    transmission: apiData.transmission,
    combinedMpg: apiData.combined_mpg,
    normalizedColorExterior: apiData.normalized_color_exterior,
    normalizedColorInterior: apiData.normalized_color_interior,
  };
}

// facets

function transformRecord<T extends Record<string, number | undefined>>(
  apiData?: T
): Record<string, number> {
  if (!apiData) return {};
  return Object.entries(apiData).reduce((acc, [key, value]) => {
    acc[key] = value ?? 0;
    return acc;
  }, {} as Record<string, number>);
}

export function transformRearWheel(apiData?: Type.RearWheelFacetResponse): Type.RearWheelFacet {
  return transformRecord(apiData);
}

function transformDriveTrain(apiData?: Type.DriveTrainFacetResponse): Type.DriveTrainFacet {
  if (!apiData) return {};

  return {
    frontWheel: apiData.FWD ?? 0,
    rearWheel: apiData.RWD ?? 0,
    allWheel: apiData.AWD ?? 0,
    fourWheel: apiData['4X4'] ?? 0,
  };
}

function transformFeaturesTechnology(apiData?: Type.FeaturesFacetResponse): Type.FeaturesFacet {
  if (!apiData) return {};

  const result: Type.FeaturesFacet = {};

  if (apiData.backup_camera !== undefined) result.backupCamera = apiData.backup_camera ?? 0;
  if (apiData.bluetooth !== undefined) result.bluetooth = apiData.bluetooth ?? 0;
  if (apiData.entertainment !== undefined) result.entertainmentDVD = apiData.entertainment ?? 0;
  if (apiData.handicap_accessible !== undefined)
    result.handicapAccessible = apiData.handicap_accessible ?? 0;
  if (apiData.heated_seats !== undefined) result.heatedSeats = apiData.heated_seats ?? 0;
  if (apiData.ipod_aux_input !== undefined) result.iPodAUXInput = apiData.ipod_aux_input ?? 0;
  if (apiData.lane_departure_warning_system !== undefined)
    result.laneDepartureWarning = apiData.lane_departure_warning_system ?? 0;
  if (apiData.leather !== undefined) result.leather = apiData.leather ?? 0;
  if (apiData.navigation !== undefined) result.navigationSystem = apiData.navigation ?? 0;
  if (apiData.one_owner !== undefined) result.oneOwner = apiData.one_owner ?? 0;
  if (apiData.roof_rack !== undefined) result.roofRack = apiData.roof_rack ?? 0;
  if (apiData.sunroof !== undefined) result.sunroofMoonroof = apiData.sunroof ?? 0;
  if (apiData.third_row_seats !== undefined) result['3rdRowSeats'] = apiData.third_row_seats ?? 0;
  if (apiData.towing !== undefined) result.towing = apiData.towing ?? 0;
  if (apiData.warranty !== undefined) result.warranty = apiData.warranty ?? 0;

  return result;
}

function transformBedType(apiData?: Type.BedTypeFacetResponse): Type.BedTypeFacet {
  if (!apiData) return {};

  return {
    short: apiData.short ?? 0,
    regular: apiData.regular ?? 0,
    chassis: apiData.chassis ?? 0,
    stepSide: apiData.step_side ?? 0,
    long: apiData.long ?? 0,
  };
}

function transformCondition(apiData?: Type.ConditionFacetResponse): Type.ConditionFacet {
  if (!apiData) return {};

  return {
    new: apiData.new ?? 0,
    used: apiData.used ?? 0,
    certifiedPreOwned: apiData['certified pre-owned'] ?? 0,
  };
}

function transformFuelType(apiData?: Type.FuelTypeFacetResponse): Type.FuelTypeFacet {
  if (!apiData) return {};

  return {
    diesel: apiData.diesel ?? 0,
    flexFuel: apiData['flex fuel'] ?? 0,
    gasoline: apiData.gasoline ?? 0,
    hydrogenFuelCell: apiData['hydrogen fuel cell'] ?? 0,
    pluginHybrid: apiData['plug-in hybrid'] ?? 0,
    electric: apiData.electric ?? 0,
    naturalGas: apiData['natural gas'] ?? 0,
    hybrid: apiData.hybrid ?? 0,
  };
}

function transformCabin(apiData?: Type.CabinFacetResponse): Type.CabinFacet {
  return transformRecord(apiData);
}

function transformExteriorColor(
  apiData?: Type.ExteriorColorFacetResponse
): Type.ExteriorColorFacet {
  return transformRecord(apiData);
}

function transformInteriorColor(
  apiData?: Type.InteriorColorFacetResponse
): Type.InteriorColorFacet {
  return transformRecord(apiData);
}

function transformEngineCylinders(
  apiData?: Type.EngineCylindersFacetResponse
): Type.EngineCylindersFacet {
  return transformRecord(apiData);
}

function transformTransmission(apiData?: Type.TransmissionFacetResponse): Type.TransmissionFacet {
  return transformRecord(apiData);
}

function transformDoorCount(apiData?: Type.DoorCountFacetResponse): Type.DoorCountFacet {
  return transformRecord(apiData);
}

function transformCategory(apiData?: Type.CategoryFacetResponse): Type.CategoryFacet {
  if (!apiData) return {};

  return {
    american: apiData.american ?? 0,
    classic: apiData.classic ?? 0,
    commuter: apiData.commuter ?? 0,
    electric: apiData.electric ?? 0,
    family: apiData.family ?? 0,
    fuelEfficient: apiData.fuel_efficient ?? 0,
    hybrid: apiData.hybrid ?? 0,
    large: apiData.large ?? 0,
    luxury: apiData.luxury ?? 0,
    muscle: apiData.muscle ?? 0,
    offRoad: apiData.off_road ?? 0,
    small: apiData.small ?? 0,
    sport: apiData.sport ?? 0,
    supercar: apiData.supercar ?? 0,
  };
}

function transformBodyStyle(apiData?: Type.BodyStyleFacetResponse): Type.BodyStyleFacet {
  return transformRecord(apiData);
}

function transformMakeModels(
  apiData?: Record<string, Record<string, number>>
): Record<string, Record<string, number>> {
  if (!apiData || Object.keys(apiData).length === 0) return {};

  return Object.entries(apiData).reduce((acc, [key, value]) => {
    acc[key] = transformRecord(value);
    return acc;
  }, {} as Record<string, Record<string, number>>);
}

function transformModelTrims(
  apiData?: Record<string, Record<string, number>>
): Record<string, Record<string, number>> {
  if (!apiData || Object.keys(apiData).length === 0) return {};

  return Object.entries(apiData).reduce((acc, [key, value]) => {
    acc[key] = transformRecord(value);
    return acc;
  }, {} as Record<string, Record<string, number>>);
}

function transformFacetCounts(apiData: Type.SearchResponseFacetCounts): Type.SearchFacetCounts {
  return {
    features: transformFeaturesTechnology(apiData.features),
    bed: transformBedType(apiData.bed),
    rearWheel: transformRearWheel(apiData.rear_wheel),
    condition: transformCondition(apiData.condition),
    cabin: transformCabin(apiData.cabin),
    fuelType: transformFuelType(apiData.fuel_type),
    exteriorColor: transformExteriorColor(apiData.exterior_color),
    interiorColor: transformInteriorColor(apiData.interior_color),
    driveline: transformDriveTrain(apiData.driveline),
    engineCylinders: transformEngineCylinders(apiData.engine_cylinders),
    transmission: transformTransmission(apiData.transmission),
    doorCount: transformDoorCount(apiData.door_count),
    category: transformCategory(apiData.category),
    bodyStyle: transformBodyStyle(apiData.body_style),
    combinedMpg: transformRecord(apiData.combined_mpg),
    makes: transformRecord(apiData.makes),
    makeModels: transformMakeModels(apiData.make_models),
    modelTrims: transformModelTrims(apiData.model_trims),
  };
}

export {
  transformFacetCounts,
  transformRecord,
  transformDriveTrain,
  transformFeaturesTechnology,
  transformBedType,
  transformCondition,
  transformFuelType,
  transformCabin,
  transformExteriorColor,
  transformInteriorColor,
  transformEngineCylinders,
  transformTransmission,
  transformDoorCount,
  transformCategory,
  transformBodyStyle,
};
